import React, { useEffect, useState, useCallback } from "react"
import { connect } from "getstream"

const ACTIVITY_TYPE_LABELS = {
  product_listed: "listed a product",
  drop_launched: "launched a drop",
  store_followed: "followed a store",
  product_purchased: "purchased a product",
  review_posted: "posted a review",
}

const ActivityFeed = ({ feedGroup = "user", feedId, apiKey, appId, token, userId }) => {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextId, setNextId] = useState(null)

  const fetchActivities = useCallback(async (idLt = null) => {
    if (!apiKey || !appId || !token || !feedId) {
      setError("Missing required configuration")
      setLoading(false)
      return
    }

    try {
      const client = connect(apiKey, null, appId)
      const feed = client.feed(feedGroup, feedId, token)
      const params = { limit: 25 }
      if (idLt) params.id_lt = idLt

      const response = await feed.get(params)
      const results = response.results || []

      if (idLt) {
        setActivities(prev => [...prev, ...results])
      } else {
        setActivities(results)
      }

      setHasMore(results.length === 25)
      if (results.length > 0) setNextId(results[results.length - 1].id)
      setLoading(false)
    } catch (err) {
      console.error("ActivityFeed: Failed to fetch:", err)
      setError(`Failed to load feed: ${err.message}`)
      setLoading(false)
    }
  }, [apiKey, appId, token, feedId, feedGroup])

  useEffect(() => { fetchActivities() }, [fetchActivities])

  const loadMore = () => { if (hasMore && nextId) fetchActivities(nextId) }

  const formatTime = (timestamp) => {
    const diff = Date.now() - new Date(timestamp)
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return "just now"
    if (mins < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  const renderActivity = (activity) => {
    const verb = activity.verb || ""
    const label = ACTIVITY_TYPE_LABELS[verb] || verb
    const actorName = activity.actor?.name || activity.actor || "Someone"

    return (
      <div key={activity.id} className="cf-activity-item">
        <div className="cf-activity-item__avatar">
          {(typeof actorName === "string" ? actorName[0] : "?").toUpperCase()}
        </div>
        <div className="cf-activity-item__body">
          <div className="cf-activity-item__header">
            <strong className="cf-activity-item__actor">{actorName}</strong>
            <span className="cf-activity-item__verb"> {label}</span>
            <span className="cf-activity-item__time">{formatTime(activity.time)}</span>
          </div>
          <div className="cf-activity-item__content">
            {activity.message || activity.text || activity.object?.text || ""}
          </div>
          {activity.reaction_counts && (
            <div className="cf-activity-item__reactions">
              {activity.reaction_counts.like > 0 && <span>&#128077; {activity.reaction_counts.like}</span>}
              {activity.reaction_counts.fire > 0 && <span>&#128293; {activity.reaction_counts.fire}</span>}
              {activity.reaction_counts.comment > 0 && <span>&#128172; {activity.reaction_counts.comment}</span>}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="cf-feed-error"><p>{error}</p></div>
  }

  if (loading) {
    return (
      <div className="cf-feed-loading">
        <div className="cf-feed-loading__spinner" />
        <p>Loading activities...</p>
      </div>
    )
  }

  return (
    <div className="cf-activity-feed">
      {activities.length === 0 ? (
        <div className="cf-feed-empty">
          <p>No activities yet</p>
          <p className="cf-feed-empty__hint">Activities will appear here as they happen</p>
        </div>
      ) : (
        <>
          <div className="cf-activity-list">
            {activities.map(renderActivity)}
          </div>
          {hasMore && (
            <button className="cf-feed-load-more" onClick={loadMore}>
              Load more
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default ActivityFeed
